// @ts-check
// Prévia da mensagem de um modelo do WhatsApp como a família vê (contrato 09 de campanhas de WhatsApp, seção 18):
// balão com cabeçalho, corpo, rodapé e botões, variáveis destacadas e, quando há, a navegação entre exemplos de quem
// recebe (editor da campanha e nó do fluxo). Usada pelo formulário e pela página do modelo (1D), pela campanha (2B)
// e pelos fluxos (2D).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { splitVariableSegments } from '../../lib/whatsapp-templates.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {{ headerText?: string | null, bodyText?: string | null, footerText?: string | null, buttons?: Array<{ type?: string, text?: string }> | null }} PreviewTemplate
 * @typedef {{ label?: string, values: Record<string, string>, fallbackVariables?: string[] }} PreviewSample
 */

const BUTTON_ICONS = /** @type {Record<string, string>} */ ({ QUICK_REPLY: 'IconArrowBackUp', URL: 'IconExternalLink', PHONE_NUMBER: 'IconPhone' });

/**
 * Texto com as variáveis em destaque: preenchidas com o valor (o valor reserva em outra cor) ou como {{nome}}.
 * @param {{ text: string | null | undefined, values: Record<string, string>, fallbackVariables?: string[] }} props
 */
function PreviewText({ text, values, fallbackVariables = [] }) {
  return html`${splitVariableSegments(text).map((segment, index) => {
    if (segment.kind === 'text') return html`<span key=${index}>${segment.value}</span>`;
    const value = values[segment.value];
    return value
      ? html`<span key=${index} class="wa-preview-variable" data-filled="true" data-fallback=${fallbackVariables.includes(segment.value) ? 'true' : undefined}
          title=${fallbackVariables.includes(segment.value) ? t('whatsappCampaigns.variables.usedFallback') : undefined}>${value}</span>`
      : html`<span key=${index} class="wa-preview-variable" data-filled="false">{{${segment.value}}}</span>`;
  })}`;
}

/**
 * @param {{ template: PreviewTemplate | null, values?: Record<string, string>, samples?: PreviewSample[] | null, title?: string | null,
 *   emptyText?: string, ariaLabel?: string }} props
 *   values: valores fixos (exemplos do formulário); samples: exemplos de quem recebe, com navegação (vence values)
 */
export function WhatsappMessagePreview({ template, values = {}, samples = null, title, emptyText, ariaLabel }) {
  const [sampleIndex, setSampleIndex] = useState(0);
  const sampleList = Array.isArray(samples) ? samples : [];
  const currentIndex = Math.min(sampleIndex, Math.max(sampleList.length - 1, 0));
  const sample = sampleList[currentIndex] ?? null;
  const shownValues = sample ? sample.values : values;
  const fallbackVariables = sample?.fallbackVariables ?? [];
  const heading = title === null ? null : title ?? t('whatsappCampaigns.preview.title');
  const buttons = (template?.buttons ?? []).filter((button) => button?.text);
  return html`<section class="wa-preview" aria-label=${ariaLabel ?? heading ?? t('whatsappCampaigns.preview.title')}>
    ${heading ? html`<span class="wa-preview-title">${heading}</span>` : null}
    <div class="wa-preview-screen">
      ${template
        ? html`<div class="wa-preview-bubble">
            ${template.headerText ? html`<p class="wa-preview-header"><${PreviewText} text=${template.headerText} values=${shownValues} fallbackVariables=${fallbackVariables} /></p>` : null}
            <p class="wa-preview-body"><${PreviewText} text=${template.bodyText} values=${shownValues} fallbackVariables=${fallbackVariables} /></p>
            ${template.footerText ? html`<p class="wa-preview-footer">${template.footerText}</p>` : null}
          </div>
          ${buttons.length > 0
            ? html`<div class="wa-preview-buttons">
                ${buttons.map((button, index) => html`<span key=${index} class="wa-preview-button">
                  <${Icon} name=${BUTTON_ICONS[button.type ?? ''] ?? 'IconArrowBackUp'} size=${14} />${button.text}
                </span>`)}
              </div>`
            : null}`
        : html`<p class="wa-preview-empty">${emptyText ?? t('whatsappCampaigns.preview.empty')}</p>`}
    </div>
    ${sampleList.length > 1
      ? html`<div class="wa-preview-nav">
          <${LightIconButton} icon="IconChevronLeft" ariaLabel=${t('whatsappCampaigns.preview.previous')} disabled=${currentIndex === 0}
            onClick=${() => setSampleIndex(Math.max(currentIndex - 1, 0))} />
          <span aria-live="polite">${sample?.label ? `${sample.label} · ` : ''}${t('whatsappCampaigns.preview.position', { current: currentIndex + 1, total: sampleList.length })}</span>
          <${LightIconButton} icon="IconChevronRight" ariaLabel=${t('whatsappCampaigns.preview.next')} disabled=${currentIndex >= sampleList.length - 1}
            onClick=${() => setSampleIndex(Math.min(currentIndex + 1, sampleList.length - 1))} />
        </div>`
      : null}
  </section>`;
}
